import Box from "@mui/material/Box";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import Dashboard from "./Dashboard/Dashboard";
import Forms from "./Forms/Forms";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import Lookup from "./Lookup/Lookup";
import UiSettings from "./UiSettings/UiSettings";
import Features from "./Features/Features";
import Login from "./Login/Login";
import { getCookie } from "../utils/Utility";
import { useAppSelector } from "../store/hooks";
import { Alert, Slide, SlideProps, Snackbar } from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import SecretKeys from "./SecretKeys/SecretKeys";
import HeaderandNavbar from "./components/HeaderandNavbar";
import Settings from "./Settings/Settings";
import UserManagement from "./UserManagement/UserManagement";
import LookupMaster from "./Lookup/LookupMaster";
import RegisterAdmin from "./RegisterAdmin/RegisterAdmin";
import LoginActivity from "./LoginActivity/LoginActivity";
import CMSDashboard from "./CMS/CMSDashboard";
import ConversationAnalysis from "./ConversationAnalysis/ConversationAnalysis";
import InsureAssistPerformance from "./InsureAssistPerformance/InsureAssistPerformance";
import CommunicationTemplates from "./CommunicationTemplates/CommunicationTemplates";
import PromptManagement from "./PromptManagement/PromptManagement";
import ResetPassword from "./Login/ResetPassword";
import PasswordConfiguration from "./PasswordConfiguration/PasswordConfiguration";
import RoleAndPermission from "./RoleAndPermission/RoleAndPermission";
import CustomerAnalytics from "./CustomerAnalytics/CustomerAnalytics";
import Layout from "./components/Layout";

function SlideTransition(props: SlideProps) {
  return <Slide {...props} direction="down" />;
}

const AdminRoutes = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isToken = searchParams.get("email");
  const authenticated = getCookie("authenticated");
  const roleCode = getCookie("roleCode");
  const isPasswordExpired = getCookie("isPasswordExpired") === "true";
  const { isSuccess, calledReducerType } = useAppSelector(
    (state) => state.common
  );

  const [pageName, setPageName] = useState("Dashboard");
  const [snackbarState, setSnackbarState] = useState<{
    open: boolean;
    Transition: React.ComponentType<
      TransitionProps & {
        children: React.ReactElement<any, any>;
      }
    >;
  }>({ open: false, Transition: SlideTransition });

  const handleSnackbarClose = useCallback(() => {
    setSnackbarState((prevState) => ({ ...prevState, open: false }));
  }, []);

  const routes: ReactElement = useMemo(
    () => (
      <>
        {authenticated && roleCode === "1" ? (
          <>
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/forms" element={<Forms />} />
            <Route path="/lookup" element={<LookupMaster />} />
            <Route path="/lookup/:name" element={<Lookup />} />
            <Route path="/uiconfiguration" element={<UiSettings />} />
            <Route path="/features" element={<Features />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/secretkeys" element={<SecretKeys />} />
            <Route path="/usermanagement" element={<UserManagement />} />
            <Route path="/loginreports" element={<LoginActivity />} />
            <Route path="/cms" element={<CMSDashboard />} />
            <Route
              path="/conversation-analysis"
              element={<ConversationAnalysis />}
            />
            <Route
              path="/insure-assist-performance"
              element={<InsureAssistPerformance />}
            />
            <Route
              path="/communication-templates"
              element={<CommunicationTemplates />}
            />
            <Route
              path="/password-configuration"
              element={<PasswordConfiguration />}
            />
            <Route path="/roles-permissions" element={<RoleAndPermission />} />
            <Route path="/prompt-management" element={<PromptManagement />} />
            <Route path="/customer-analytics" element={<CustomerAnalytics />} />
          </>
        ) : (
          <>
            <Route index element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterAdmin />} />
          </>
        )}
        <Route path="/reset-password" element={<ResetPassword />} />
      </>
    ),
    [authenticated, roleCode]
  );

  useEffect(() => {
    const pageName = location.pathname.split("/")[2];
    if (
      (!authenticated || roleCode !== "1") &&
      pageName !== "register" &&
      !isToken
    ) {
      navigate("/admin/login");
    }
  }, [authenticated, roleCode, navigate, calledReducerType, isSuccess]);

  useEffect(() => {
    const pageName = location.pathname.split("/")[2];
    if (pageName) {
      setPageName(pageName.charAt(0).toUpperCase() + pageName.slice(1));
    } else {
      setPageName("Dashboard");
    }
  }, [location]);

  useEffect(() => {
    if (isSuccess && calledReducerType === "adminAuth/adminLogin") {
      setSnackbarState((prevState) => ({ ...prevState, open: true }));
    }
  }, [isSuccess, calledReducerType]);

  return (
    <>
      <Layout>
        <Routes>{routes}</Routes>
      </Layout>
      {!isPasswordExpired && authenticated && (
        <Snackbar
          anchorOrigin={{ horizontal: "center", vertical: "top" }}
          open={snackbarState.open}
          TransitionComponent={snackbarState.Transition}
          autoHideDuration={3000}
          onClose={handleSnackbarClose}
        >
          <Alert severity="success" variant="filled">
            Logged in successfully
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default AdminRoutes;
